// Where a password-reset link lands (`/auth/reset-password/:token`).
//
// It wore the old react-bootstrap card - a centred "Reset Password" heading
// over a green box, and a button labelled "Click here to Login : 6" - while
// every other page a signed-out person meets had moved to AuthLayout. It now
// arrives on the same sheet, beside the same picture, in the same words.
//
// **The two outcomes are told apart.** The old screen ran one countdown
// whatever happened and sent the reader to the sign-in page either way, so
// somebody whose link had expired was moved on before they could read why.
// A link that worked says so and offers the way in; a link that didn't keeps
// the reader here and offers a new one.
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

import AuthLayout from "../auth/AuthLayout";
import { resetPassword } from "../../actions";

// Long enough to read the sentence, short enough not to be a wait. Only ever
// runs where the reset worked.
const SECONDS = 5;

// The same rounded mark the other auth pages open with; the colour is what
// says which outcome this is.
function Mark({ ok, children }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 56,
        height: 56,
        borderRadius: 3,
        display: "grid",
        placeItems: "center",
        mb: 3,
        bgcolor: ok ? "primary.main" : "error.light",
        color: ok ? "secondary.main" : "error.dark",
      }}
    >
      {children}
    </Box>
  );
}

export default function ResetPasswordMsg() {
  const history = useHistory();
  const { token } = useParams();
  // null while the server is still being asked - the reader is told it is
  // working rather than shown an outcome that hasn't happened.
  const [result, setResult] = useState(null);
  const [counter, setCounter] = useState(SECONDS);

  const toLogin = useCallback(() => {
    history.push("/login");
    // The app reads the signed-in state once, at start-up: a fresh load is
    // what makes the sign-in page ready for the new password.
    history.go(0);
  }, [history]);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const response = await resetPassword(token);
        if (live) setResult({ ok: Boolean(response?.success), msg: response?.msg || "" });
      } catch (err) {
        // A reset that can't be confirmed is not a reset: say so plainly
        // rather than sending the reader to sign in with a password that may
        // never have been set.
        if (live) setResult({ ok: false, msg: "" });
      }
    })();
    return () => {
      live = false;
    };
  }, [token]);

  // Only a reset that worked moves on by itself.
  useEffect(() => {
    if (!result?.ok) return undefined;
    if (counter <= 0) {
      toLogin();
      return undefined;
    }
    const timer = setTimeout(() => setCounter((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [result, counter, toLogin]);

  if (!result) {
    return (
      <AuthLayout>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="subtitle1">Setting your new password…</Typography>
        </Stack>
      </AuthLayout>
    );
  }

  if (result.ok) {
    return (
      <AuthLayout>
        <Mark ok>
          <CheckRoundedIcon />
        </Mark>
        <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
          Your password is set
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 4 }}>
          {result.msg || "You can sign in with your new password now."}
        </Typography>
        <Stack spacing={1.5}>
          <Button onClick={toLogin} variant="contained" size="large" fullWidth>
            Go to sign in
          </Button>
          {/* What is about to happen, said beside the button rather than
              inside its label - a button says what it does, not how long
              until it does it by itself. */}
          <Typography variant="caption" color="text.secondary" aria-live="polite">
            Taking you there in {counter} second{counter === 1 ? "" : "s"}.
          </Typography>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Mark ok={false}>
        <ErrorOutlineRoundedIcon />
      </Mark>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        This link didn't work
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 4 }}>
        {result.msg || "It may have been used already, or it may have expired. Ask for a new one and we'll send it straight away."}
      </Typography>
      <Stack spacing={1.5}>
        <Button component={RouterLink} to="/forget-password" variant="contained" size="large" fullWidth>
          Send me a new link
        </Button>
        <Button component={RouterLink} to="/login" variant="text">
          Back to sign in
        </Button>
      </Stack>
    </AuthLayout>
  );
}
